import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import { Toaster, toast } from "react-hot-toast";
import { AuthContext } from "../../../context/AuthContext";
import API from "../../../services/interceptor";

const ClientDetailsForm = ({ userId, onSuccess, onCancel }) => {
  const { loginUser } = useContext(AuthContext);

  // State to hold form data, initialized with default values
  const [formData, setFormData] = useState({
    name: "",
    dateOfBirth: "",
    gender: "",
    avatar: "",
    contact: {
      mobile: "",
      email: "",
      address: "",
      map: "",
    },
  });

  // State to manage loading status to provide UI feedback
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLocationLoading, setIsLocationLoading] = useState(false);
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);

  // Fetch existing user data when the component mounts
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        // Fetch the main user object to get the userDetails reference
        const res = await API.get(`/api/user/${userId}`);
        if (res.data && res.data.userDetails) {
          const { userDetails } = res.data;

          // Format the date correctly for the input field
          const formattedDate = userDetails.dateOfBirth
            ? new Date(userDetails.dateOfBirth).toISOString().split("T")[0]
            : "";

          // Pre-fill the form with existing data
          setFormData({
            name: userDetails.name || "",
            dateOfBirth: formattedDate,
            gender: userDetails.gender || "",
            avatar: userDetails.avatar || "",
            contact: {
              mobile: userDetails.contact?.mobile || "",
              email: userDetails.contact?.email || "",
              address: userDetails.contact?.address || "",
              map: userDetails.contact?.map || "",
            },
          });
        }
      } catch (error) {
        // If user details don't exist, the form will remain empty, which is expected.
        // We only log errors that are not 404s (Not Found).
        if (error.response && error.response.status !== 404) {
          toast.error("Failed to fetch user data.");
          console.error("Fetch user data error:", error);
        }
      } finally {
        setIsLoading(false);
      }
    };

    if (userId) {
      fetchUserData();
    }
  }, [userId]);

  // Handle changes in form inputs, including nested contact object
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith("contact.")) {
      const contactField = name.split(".")[1];
      setFormData((prevData) => ({
        ...prevData,
        contact: {
          ...prevData.contact,
          [contactField]: value,
        },
      }));
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  // Function to get current location from device
  const getCurrentLocation = () => {
    setIsLocationLoading(true);

    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by this browser.");
      setIsLocationLoading(false);
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 60000,
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const locationString = `${latitude}, ${longitude}`;

        setFormData((prevData) => ({
          ...prevData,
          contact: {
            ...prevData.contact,
            map: locationString,
          },
        }));

        toast.success("Location fetched successfully!");
        setIsLocationLoading(false);
      },
      (error) => {
        let errorMessage = "Failed to get location. ";

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage += "Location access denied by user.";
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage += "Location information unavailable.";
            break;
          case error.TIMEOUT:
            errorMessage += "Location request timed out.";
            break;
          default:
            errorMessage += "An unknown error occurred.";
            break;
        }

        toast.error(errorMessage);
        setIsLocationLoading(false);
      },
      options
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      console.log("Updated details:", formData);

      // Send a POST request to the backend to create or update user details
      const res = await API.post(`/api/user/${userId}/details`, formData);
      toast.success("Profile updated successfully!");
      console.log("Updated details:", res.data);

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      toast.error("Failed to update profile. Please try again.");
      console.error("Update profile error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Corrected avatar upload function
  const handleAvatarUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error(
        "Please select a valid image file (JPEG, PNG, GIF, or WebP)."
      );
      return;
    }

    // Validate file size (e.g., max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      toast.error("File size must be less than 5MB.");
      return;
    }

    setIsUploadingAvatar(true);

    const uploadFormData = new FormData();
    uploadFormData.append("file", file);
    uploadFormData.append("upload_preset", "mypreset");
    uploadFormData.append("cloud_name", "dsi1mb2aj");

    try {
      const response = await fetch(
        `https://api.cloudinary.com/v1_1/dsi1mb2aj/image/upload`,
        {
          method: "POST",
          body: uploadFormData,
        }
      );

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      const avatarUrl = data.secure_url;

      // Update the form data with the new avatar URL
      setFormData((prevData) => ({
        ...prevData,
        avatar: avatarUrl,
      }));

      toast.success("Avatar uploaded successfully!");
    } catch (error) {
      console.error("Error uploading avatar:", error);
      toast.error("Failed to upload avatar. Please try again.");
    } finally {
      setIsUploadingAvatar(false);
    }
  };

  // Show a loading spinner while fetching initial data
  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="w-6 h-6 border-2 border-gray-300 border-t-slate-800 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <>
      <Toaster
        position="top-center"
        toastOptions={{
          duration: 3000,
          style: {
            background: "#374151",
            color: "#ffffff",
            fontSize: "14px",
            borderRadius: "6px",
          },
          success: {
            iconTheme: {
              primary: "#10b981",
              secondary: "#ffffff",
            },
          },
          error: {
            iconTheme: {
              primary: "#ef4444",
              secondary: "#ffffff",
            },
          },
        }}
      />

      <div className="max-w-7xl mx-auto py-8 px-4">
        <div className="bg-white border border-gray-200 rounded-lg">
          {/* Header */}
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center space-x-4">
              <div className="relative">
                <div className="w-16 h-16 bg-gray-100 rounded-full overflow-hidden">
                  <img
                    src={
                      formData.avatar || `https://i.pravatar.cc/150?u=${userId}`
                    }
                    alt="Profile"
                    className="w-full h-full object-cover"
                  />
                  {isUploadingAvatar && (
                    <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  )}
                </div>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">
                  Profile Settings
                </h1>
                <p className="text-sm text-gray-500">
                  Update your personal information
                </p>
              </div>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="px-6 py-6 space-y-6">
            {/* Personal Information */}
            <div className="space-y-4">
              <h2 className="text-sm font-medium text-gray-900">
                Personal Information
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Full Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="Enter your full name"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="dateOfBirth"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Date of Birth
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                  />
                </div>

                <div>
                  <label
                    htmlFor="gender"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Gender
                  </label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                  >
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="avatar"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Profile Photo
                  </label>
                  <input
                    type="file"
                    id="avatar"
                    accept="image/*"
                    onChange={handleAvatarUpload}
                    disabled={isUploadingAvatar}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-gray-50 file:text-gray-700 hover:file:bg-gray-100 disabled:opacity-50"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    JPEG, PNG, GIF, or WebP. Max 5MB.
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Information */}
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h2 className="text-sm font-medium text-gray-900">
                Contact Information
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="contact.email"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    id="contact.email"
                    name="contact.email"
                    value={formData.contact.email}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="email@example.com"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="contact.mobile"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Phone
                  </label>
                  <input
                    type="tel"
                    id="contact.mobile"
                    name="contact.mobile"
                    value={formData.contact.mobile}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="+1 (555) 000-0000"
                    required
                  />
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="contact.address"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Address
                  </label>
                  <input
                    type="text"
                    id="contact.address"
                    name="contact.address"
                    value={formData.contact.address}
                    onChange={handleChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                    placeholder="Street address, city, state, zip code"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label
                    htmlFor="contact.map"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Location
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      id="contact.map"
                      name="contact.map"
                      value={formData.contact.map}
                      onChange={handleChange}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-gray-500 focus:border-gray-500"
                      placeholder="Coordinates or location URL"
                    />
                    <button
                      type="button"
                      onClick={getCurrentLocation}
                      disabled={isLocationLoading}
                      className="px-3 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                    >
                      {isLocationLoading ? (
                        <div className="flex items-center space-x-1">
                          <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                          <span>Getting...</span>
                        </div>
                      ) : (
                        "Location"
                      )}
                    </button>
                  </div>
                  {formData.contact.map &&
                    formData.contact.map.includes(",") && (
                      <div className="mt-2">
                        <a
                          href={`https://www.google.com/maps?q=${formData.contact.map}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-gray-600 hover:text-gray-900 underline"
                        >
                          View on Google Maps →
                        </a>
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="pt-6 border-t border-gray-200">
              <div className="flex justify-end space-x-3">
                {onCancel && (
                  <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50 focus:outline-none focus:ring-1 focus:ring-gray-500 transition-colors"
                  >
                    Cancel
                  </button>
                )}
                <button
                  type="submit"
                  disabled={isSubmitting || isUploadingAvatar}
                  className="px-4 py-2 bg-gray-900 text-white text-sm rounded-md hover:bg-gray-800 focus:outline-none focus:ring-1 focus:ring-gray-500 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  {isSubmitting ? "Saving..." : "Save"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default ClientDetailsForm;
